<template>
    <div class="container">
        <div class="title-container">
            <span>
                {{ $t('devis.request.title') }}
                <ReglePastille cle="demandeDevis.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class='filter-container'>
                <Filter
                    @search="doFilter"
                    @setDemandeDevisId="this.setDemandeDevisId"
                    @setIsMine="this.setIsMine"
                    @clearFilter="this.clearFilter"
                    @statusToggleSelectAll="this.statusToggleSelectAll"
                    @removeFilterStatus='removeFilterStatus'
                    @addFilterStatus="this.addFilterStatus"
                    :filter="this.filter"
                    :status-list='statusList'
                />
            </div>
            <div class="dataTable">
                <div>
                    <h2>
                        {{ this.count }}
                        {{getTitreTableau}}
                        <ReglePastille cle="suiviDemandeDevis.portee" />
                    </h2>
                    <BorderedIconButton v-if="!isExportDisabled" @click="exportXls" :imgUrl="iconeXls" :title="$t('list.export')"/>
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" @scroll="handleScroll" class="datatable-container">
                    <DataTable
                        :isLoading="isLoading"
                        :count="+count"
                        :tableData="this.tableData"
                        :tableHeader="this.tableHeader"
                        @sort="doSort"
                        @select="goToPage"
                    />
                </div>
            </div>
        </div>
        <WaitingModal v-if='isExportXlsLoading'/>
    </div>
</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import { SortType, sortValue } from '@/models/SortType';
import { defineComponent, onMounted, ref } from 'vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { DEMANDE_DEVIS } from '../store/mutation-types';
import { DemandeDevisActions } from '../store/action-types';
import { DemandeDevis, EtatDemandeDevis } from '@/models/DemandeDevis';
import { TableHeader } from '@/models/TableHeader';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import router from '@/router';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import dayjs from 'dayjs';
import i18n from '@/plugins/i18n';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    statusList: string[];
    filter: {
        isMine: boolean;
        demandeDevisId: string;
        statusList: string[];
    };
}

export default defineComponent({
    name: 'ListDemandeDevisPage',
    components: { WaitingModal, BorderedIconButton, Filter, DataTable, LoaderComponent, ReglePastille },
    data(): Data {
        return {
            statusList: [],
            filter: {
                isMine: false,
                demandeDevisId: '',
                statusList: [],
            },
        };
    },
    beforeMount() {
        this.statusList = Object.values(EtatDemandeDevis);
        this.filter.statusList = this.statusList;
        this.filter.demandeDevisId = this.filterDemandeDevisId;
        this.filter.isMine = this.filterMine;

        if (this.tableData.length === 0) {
            this.getDemandesDevisData();
        }
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    methods: {
        addFilterStatus(status: string) {
            this.filter.statusList = [...this.filter.statusList, status];
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(statusData => statusData !== status);
        },
        statusToggleSelectAll() {
            this.filter.statusList = this.filter.statusList.length === this.statusList.length ? [] : this.statusList;
        },
        getDemandesDevisData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            this.$store.dispatch(`demandeDevisModuleState/${DemandeDevisActions.GET_DEMANDES_DEVIS}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                id: this.filterDemandeDevisId !== '' ? this.filterDemandeDevisId : undefined,
                mesDemandes: this.filterMine,
                status: this.filterStatusList
            });
        },
        exportXls() : void {
            this.$store.dispatch(`demandeDevisModuleState/${DemandeDevisActions.GET_XLS_DEMANDE_DEVIS}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                id: this.filterDemandeDevisId !== '' ? this.filterDemandeDevisId : undefined,
                mesDemandes: this.filterMine,
            });
        },
        doFilter() {
            this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.RESET_ITEMS}`);

            this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.SET_FILTER_ONLY_MINE}`, this.filter.isMine);
            this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.SET_FILTER_DEMANDE_DEVIS_ID}`, this.filter.demandeDevisId);
            this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.SET_FILTER_DEMANDE_DEVIS_STATUS_LIST}`, this.filter.statusList);

            this.getDemandesDevisData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.SET_SORT}`, { sortData, sortStatus });
            this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.RESET_ITEMS}`);
            this.getDemandesDevisData();
        },
        setDemandeDevisId(value: string): void {
            this.filter.demandeDevisId = value;
        },
        setIsMine(value: boolean) {
            this.filter.isMine = value;
        },
        clearFilter() {
            this.filter.demandeDevisId = '';
            this.filter.isMine = false;

            this.doFilter();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.SET_PAGE}`, this.page + 1);
                this.getDemandesDevisData(this.page);
            }
        },
        goToPage(item: { id: { pageName: string; value: number; }; }){
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
    },
    computed: {
        tableData() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisItems'].map(({ id, objet, devis, status, site, date }: DemandeDevis) => ({
                id: { pageName: 'devisRequestDetailView', value: id, label: id },
                objet,
                devisIds: devis ? devis.map(devis => devis.numero+'-'+devis.version).join(', ') : '',
                date: date == null ? '' : dayjs(date).format('DD/MM/YYYY'),
                site: site,
                statut: statusName(status)
            }));
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisSort'];
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisTableHeader'];
        },
        page() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisPage'];
        },
        count() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisCount'];
        },
        numberOfPages() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisNbOfPages'];
        },
        scroll() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisScroll'];
        },
        isLoading() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisIsLoading'];
        },
        filterSite(): any[] {
            return this.$store.getters['demandeDevisModuleState/getDemandeDevisFilterSite'];
        },
        filterStatus(): string[] {
            return this.$store.getters['demandeDevisModuleState/getDemandeDevisFilterStatus'];
        },
        filterDemandeDevisId() {
            return this.$store.getters['demandeDevisModuleState/getDemandeDevisFilterDemandeDevisId'];
        },
        filterMine(): boolean {
            return this.$store.getters['demandeDevisModuleState/getDemandeDevisFilterMine'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportDisabled(): boolean {
            return this.tableData.length === 0;
        },
        isExportXlsLoading(): boolean {
            return this.$store.getters['demandeDevisModuleState/getXlsIsLoading'];
        },
        filterStatusList(): string[] {
            return this.$store.getters['demandeDevisModuleState/getFilterStatusList'];
        },
        getTitreTableau() : string {
            const param = {count: this.count};
            return i18n.global.t('devis.request.dataTable.title', param);
        }
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

:deep(.containerBtn) {
    height: 24px;
}
:deep(.icon) {
    height: 40px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
</style>
